import * as S from './styles';

import DropdownBox from '@components/Common/DropdownBox/defaultDropdown';
import SelectedBox from '@components/Common/DropdownBox/selectedBox';
import Switch from '@components/Common/Switch';

import InfoLineIcon from '@svgIcons/widget_info.svg';
import PanelOptionMixin from '../../mixin';

import {
    SELECTION_NONE,
    ASC,
    DESC,
    GRAPH_FILTER_TYPES,
    DEFAULT_DATA_LIMIT,
    PANEL_OPTION_KEYS,
    CHART_TYPES,
} from '@consts';

const { LIMIT, SORT, HIDE_OTHERS } = GRAPH_FILTER_TYPES;
const { PIE, DONUT, BAR, HORIZONTAL_BAR, LINE } = CHART_TYPES;

import PieChartIcon from '@svgIcons/pie_chart.svg';
import { listIncludes, nullablePathOr } from '@/utils/utils';

const LIMIT_OPTIONS = [SELECTION_NONE, 5, 10, 15, 20];
const SORT_OPTIONS = [ASC, DESC];

const MENU_KEYS = [LIMIT, SORT, HIDE_OTHERS];

const OPTIONS_MAP = {
    [LIMIT]: LIMIT_OPTIONS,
    [SORT]: SORT_OPTIONS,
};

const LOCALE_KEY_MAP = {
    [LIMIT]: 'count',
    [SORT]: 'sort_order',
    [HIDE_OTHERS]: 'hide_others',
};

const SELECT_TYPE = {
    DROPDOWN: 'dropdown',
    TOGGLE: 'toggle',
};

const SELECT_TYPE_MAP = {
    [LIMIT]: SELECT_TYPE.DROPDOWN,
    [SORT]: SELECT_TYPE.DROPDOWN,
    [HIDE_OTHERS]: SELECT_TYPE.TOGGLE,
};

export default {
    name: 'WidgetSettingPanelDataMenu',
    mixins: [PanelOptionMixin],
    data() {
        return {
            activeDropbox: '',
        };
    },
    computed: {
        [LIMIT]: nullablePathOr(DEFAULT_DATA_LIMIT, ['graphOptions', 'limit']),
        [HIDE_OTHERS]: R.pathOr(false, ['graphOptions', 'hideOthers']),
        [SORT]: R.compose(
            R.ifElse(R.equals(true), R.always(DESC), R.always(ASC)),
            R.pathOr(true, ['graphOptions', 'sort', 'isDesc'])
        ),
        isSortable: R.compose(
            listIncludes([PIE, DONUT, BAR, HORIZONTAL_BAR, LINE]),
            R.pathOr('', ['graphOptions', 'selectedGraphType'])
        ),
    },
    methods: {
        getRefinedOptions() {
            const { isSortable } = this;
            let menuKeys = MENU_KEYS;
            if (!isSortable) {
                menuKeys = R.without([SORT], menuKeys);
            }
            return menuKeys;
        },
        updateGraphOptions({ key, value }) {
            const { graphOptions } = this;

            this.$emit('updateByKey', {
                key: PANEL_OPTION_KEYS.GRAPH_OPTIONS,
                value: R.mergeDeepLeft({ [key]: value }, graphOptions),
            });
        },
        updateOption({ key, value }) {
            this.updateGraphOptions({
                key,
                value: key === SORT ? { isDesc: value === DESC } : value,
            });
        },
        getTranslator({ key, text }) {
            if (key === LIMIT) {
                return text === SELECTION_NONE
                    ? this.$t('NEW_DASHBOARD.all')
                    : text;
            }

            if (text === SELECTION_NONE) {
                return this.$t('NEW_DASHBOARD.none');
            }

            return this.$t(`NEW_DASHBOARD.${text}`);
        },

        getDropdownMenuItem({ key }) {
            const { activeDropbox, updateOption, getTranslator } = this;

            const optionList = OPTIONS_MAP[key];
            const selectedOption = R.path([key], this);

            if (!optionList.length > 0) {
                return null;
            }

            return (
                <S.SelectedBoxWrapper>
                    <SelectedBox
                        selectedOption={selectedOption}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            this.activeDropbox =
                                activeDropbox === key ? '' : key;
                        }}
                        allowEmptyValue={true}
                        translator={() =>
                            getTranslator({
                                key,
                                text: selectedOption,
                            })
                        }
                    />

                    <S.DropdownBoxWrapper>
                        <DropdownBox
                            width={248}
                            isVisible={activeDropbox === key}
                            optionList={optionList}
                            selectedOption={selectedOption}
                            onConfirm={(value) => {
                                updateOption({
                                    key,
                                    value,
                                });
                                this.activeDropbox = '';
                            }}
                            onClickOutside={() => (this.activeDropbox = '')}
                            translator={(text) => getTranslator({ key, text })}
                            allowEmptyValue={true}
                        />
                    </S.DropdownBoxWrapper>
                </S.SelectedBoxWrapper>
            );
        },

        getToggleMenuItem({ key }) {
            const { updateOption } = this;
            const selectedOption = R.path([key], this);

            return (
                <Switch
                    value={selectedOption}
                    type="big"
                    onClick={() => {
                        updateOption({
                            key,
                            value: !selectedOption,
                        });
                    }}
                />
            );
        },

        getTooltip({ key }) {
            if (key !== HIDE_OTHERS) {
                return null;
            }
            const localeKey = LOCALE_KEY_MAP[key];
            return this.wrapTooltip({
                visible: true,
                content: <InfoLineIcon class="info-line-icon" />,
                title: this.$t(`NEW_DASHBOARD.${localeKey}_tooltip`),
            });
        },

        getMenuItems() {
            const {
                getDropdownMenuItem,
                getToggleMenuItem,
                getTooltip,
                getRefinedOptions,
            } = this;
            const options = getRefinedOptions();

            return options.map((key) => {
                const titleLocaleKey = LOCALE_KEY_MAP[key];
                const selectType = SELECT_TYPE_MAP[key];

                if (!titleLocaleKey || !selectType) {
                    return null;
                }

                return (
                    <S.MenuItem>
                        <h4 class="title">
                            {this.$t(`NEW_DASHBOARD.${titleLocaleKey}`)}
                            {getTooltip({ key })}
                        </h4>
                        {selectType === SELECT_TYPE.DROPDOWN &&
                            getDropdownMenuItem({ key })}
                        {selectType === SELECT_TYPE.TOGGLE &&
                            getToggleMenuItem({ key })}
                    </S.MenuItem>
                );
            });
        },
    },
    render() {
        const { getMenuItems } = this;

        return (
            <S.ComponentWrapper>
                <S.HeaderWrapper>
                    <div class="title">
                        <PieChartIcon
                            class="icon"
                            v-view-box
                            width="16px"
                            height="16px"
                        />
                        <h3>{this.$t('NEW_DASHBOARD.display_settings')}</h3>
                    </div>
                </S.HeaderWrapper>
                <S.MenuWrapper>{getMenuItems()}</S.MenuWrapper>
            </S.ComponentWrapper>
        );
    },
};
